import ENDPOINTS from 'api/endpoints';
import { doDelete, doGet, doPatch, doPost, doPut } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import { generateNextPath } from 'lib/routeHelper';
import type { AudioCacheDetails, AudioCachesResponse } from './types';

export const NUM_CACHED_AUDIOS_PER_PAGE = 50;

export const getCachedAudios = (
  accountId: string,
  projectId: string,
  query: string,
  limit: number = NUM_CACHED_AUDIOS_PER_PAGE,
) => {
  return doGet<AudioCachesResponse>(
    generateNextPath(ENDPOINTS.AUDIO_CACHE, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    {
      params: {
        limit,
      },
      query,
      version: 'v2',
    },
  );
};

export const getCachedAudioDetails = async (
  accountId: string,
  projectId: string,
  entryId: string,
) => {
  return doGet<AudioCacheDetails>(
    generateNextPath(ENDPOINTS.AUDIO_CACHE_DETAILS, {
      accountId,
      projectId,
      entryId: entryId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      version: 'v2',
    },
  );
};

export const getCachedAudioFile = async (
  accountId: string,
  projectId: string,
  entryId: string,
) => {
  return doGet<Blob>(
    generateNextPath(ENDPOINTS.AUDIO_CACHE_AUDIO_FILE, {
      accountId,
      projectId,
      entryId: entryId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      version: 'v2',
      downloadingFile: true,
    },
  );
};

export const updateCachedAudioDetails = async ({
  accountId,
  projectId,
  entryId,
  data,
}: {
  accountId: string;
  projectId: string;
  entryId: string;
  data: {
    settings: {
      text: string;
      config?: AudioCacheDetails['voice_tuning_settings'];
    };
    audio: Blob;
  };
}) => {
  const formData = new FormData();
  formData.append('settings', JSON.stringify(data.settings)); // Convert settings to JSON string
  formData.append('file', data.audio);

  return doPut(
    generateNextPath(ENDPOINTS.AUDIO_CACHE_DETAILS, {
      accountId,
      projectId,
      entryId,
    }),
    LOGGER_TYPE.JUPITER,
    formData,
    {
      postingFile: true,
      version: 'v2',
    },
  );
};

export const updateCachedAudioFile = async ({
  accountId,
  projectId,
  entryId,
  data,
}: {
  accountId: string;
  projectId: string;
  entryId: string;
  data: {
    audio: Blob;
    filename: string;
  };
}) => {
  const formData = new FormData();
  formData.append('file', data.audio, data.filename);

  return doPatch(
    generateNextPath(ENDPOINTS.AUDIO_CACHE_AUDIO_FILE, {
      accountId,
      projectId,
      entryId,
    }),
    LOGGER_TYPE.JUPITER,
    formData,
    {
      postingFile: true,
      version: 'v2',
    },
  );
};

export const deleteCachedAudio = ({
  accountId,
  projectId,
  entryId,
}: {
  accountId: string;
  projectId: string;
  entryId: string;
}) => {
  return doDelete(
    generateNextPath(ENDPOINTS.AUDIO_CACHE_ENTRY, {
      accountId,
      projectId,
      entryId,
    }),
    LOGGER_TYPE.JUPITER,
    {},
    {
      version: 'v2',
    },
  );
};

export const synthesizeAudioCache = ({
  accountId,
  projectId,
  data,
  entryId,
}: {
  accountId: string;
  projectId: string;
  entryId: string;
  data: {
    text: string;
    language?: string;
    config?: AudioCacheDetails['voice_tuning_settings'];
  };
}) => {
  return doPost<Blob>(
    generateNextPath(ENDPOINTS.AUDIO_CACHE_ENTRY_SYNTHESIZE, {
      accountId,
      projectId,
      entryId,
    }),
    LOGGER_TYPE.JUPITER,
    data,
    {
      version: 'v2',
      downloadingFile: true,
    },
  );
};
